"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseCanonicalName = exports.parseSolidityJSONABI = void 0;
var js_sha3_1 = require("js-sha3");
var rlp_1 = require("rlp");
/**
 * Look through an ABI definition to see if there is a function that matches the signature provided.
 * @param sig    a 0x-prefixed hex string containing 4 bytes of info
 * @param abi    a Solidity JSON ABI structure ([external link](https://docs.ethers.io/v5/api/utils/abi/formats/#abi-formats--solidity))
 * @returns      Buffer containing RLP-serialized array of calldata info to pass to signing request
 * @public
 */
var parseSolidityJSONABI = function (sig, abi) {
    sig = coerceSig(sig);
    // Find the first match in the ABI
    var match = abi
        .filter(function (item) { return item.type === 'function'; })
        .find(function (item) {
        var def = parseDef(item);
        var funcSig = getFuncSig(def.canonicalName);
        return funcSig === sig;
    });
    if (match) {
        var def = parseDef(match).def;
        return Buffer.from((0, rlp_1.encode)(def));
    }
    return null;
};
exports.parseSolidityJSONABI = parseSolidityJSONABI;
/**
 * Convert a canonical name into an ABI definition that can be included with calldata to a general
 * signing request. Parameter names will be encoded in order that they are discovered (e.g. "1",
 * "2", "2.1", "3")
 * @param sig    a 0x-prefixed hex string containing 4 bytes of info
 * @param name   canonical name of the function
 * @returns      Buffer containing RLP-serialized array of calldata info to pass to signing request
 * @public
 */
var parseCanonicalName = function (sig, name) {
    sig = coerceSig(sig);
    if (sig !== getFuncSig(name)) {
        throw new Error('Name does not match provided sig.');
    }
    var def = [];
    // Get the function name
    var paramStart = name.indexOf('(');
    if (paramStart < 0) {
        throw new Error(BAD_CANONICAL_ERR);
    }
    def.push(name.slice(0, paramStart));
    name = name.slice(paramStart + 1);
    var paramDef = [];
    while (name.length > 1) {
        // scan until the terminating ')'
        var typeStr = popTypeStrFromCanonical(name);
        paramDef = paramDef.concat(parseTypeStr(typeStr));
        name = name.slice(typeStr.length + 1);
    }
    var parsedParamDef = parseParamDef(paramDef);
    return Buffer.from((0, rlp_1.encode)(def.concat(parsedParamDef)));
};
exports.parseCanonicalName = parseCanonicalName;
/**
 * Convert a canonical name to a function selector (a.k.a. "sig")
 * @internal
 */
function getFuncSig(canonicalName) {
    return "0x".concat((0, js_sha3_1.keccak256)(canonicalName).slice(0, 8));
}
/**
 * Ensure the sig is properly formatted
 */
function coerceSig(sig) {
    if (typeof sig !== 'string' || (sig.length !== 10 && sig.length !== 8)) {
        throw new Error('`sig` must be a hex string with 4 bytes of data.');
    }
    if (sig.length === 8) {
        sig = "0x".concat(sig);
    }
    return sig;
}
/**
 * Take the next type from a canonical definition string. Note that the string can be that of a
 * tuple. NOTE: The string should start at the index after the leading '('
 * @internal
 */
function popTypeStrFromCanonical(subName) {
    if (isTuple(subName)) {
        return getTupleName(subName);
    }
    else if (subName.indexOf(',') > -1) {
        // Normal non-tuple param
        return subName.slice(0, subName.indexOf(','));
    }
    else if (subName.indexOf(')') > -1) {
        // Last non-tuple param in the name
        return subName.slice(0, subName.indexOf(')'));
    }
    throw new Error(BAD_CANONICAL_ERR);
}
/**
 * Parse a type string, e.g. 'uint256'. Converts the string to an array of EVMParamInfo, which may
 * have nested structure if there are tuples.
 * @internal
 */
function parseTypeStr(typeStr) {
    // Non-tuples can be decoded without worrying about recursion
    if (!isTuple(typeStr)) {
        return [parseBasicTypeStr(typeStr)];
    }
    // Tuples may require recursion
    var param = {
        szBytes: 0,
        typeIdx: EVM_TYPES.indexOf('tuple'),
        arraySzs: [],
    };
    // Get the full tuple param name and separate out the array stuff
    var typeStrLessArr = getTupleName(typeStr, false);
    var typeStrArr = typeStr.slice(typeStrLessArr.length);
    param.arraySzs = getArraySzs(typeStrArr);
    // Slice off the leading paren
    typeStrLessArr = typeStrLessArr.slice(1);
    // Parse each nested param
    var paramArr = [];
    while (typeStrLessArr.length > 0) {
        var subType = popTypeStrFromCanonical(typeStrLessArr);
        typeStrLessArr = typeStrLessArr.slice(subType.length + 1);
        paramArr = paramArr.concat(parseTypeStr(subType));
    }
    // There must be at least one sub-param in the tuple
    if (!paramArr.length) {
        throw new Error(BAD_CANONICAL_ERR);
    }
    return [param, paramArr];
}
/**
 * Convert a basic type (e.g. 'uint256') from a string to EVMParamInfo type.
 * @internal
 */
function parseBasicTypeStr(typeStr) {
    var param = {
        szBytes: 0,
        typeIdx: 0,
        arraySzs: [],
    };
    var found = false;
    EVM_TYPES.forEach(function (t, i) {
        if (typeStr.indexOf(t) > -1 && !found) {
            param.typeIdx = i;
            param.arraySzs = getArraySzs(typeStr);
            var arrStart = param.arraySzs.length > 0 ? typeStr.indexOf('[') : typeStr.length;
            var typeStrNum = typeStr.slice(t.length, arrStart);
            if (parseInt(typeStrNum)) {
                param.szBytes = parseInt(typeStrNum) / 8;
                if (param.szBytes > 32) {
                    throw new Error(BAD_CANONICAL_ERR);
                }
            }
            found = true;
        }
    });
    if (!found) {
        throw new Error(BAD_CANONICAL_ERR);
    }
    return param;
}
/**
 * Parse an Etherscan definition into a calldata structure that the Lattice EVM decoder can handle
 * (EVMDef). This function may recurse if there are tuple types.
 * @internal
 */
function parseDef(item, canonicalName, def, recursed) {
    if (canonicalName === void 0) { canonicalName = ''; }
    if (def === void 0) { def = []; }
    if (recursed === void 0) { recursed = false; }
    // Function name. Can be an empty string.
    if (!recursed) {
        var nameStr = item.name || '';
        def.push(nameStr);
        canonicalName += nameStr;
    }
    // Loop through params
    if (item.inputs) {
        canonicalName += '(';
        item.inputs.forEach(function (input) {
            // Convert the input to a flat param that we can serialize
            var flatParam = getFlatParam(input);
            if (input.type.indexOf('tuple') > -1 && input.components) {
                // For tuples we need to recurse
                var recursed_1 = parseDef({ inputs: input.components }, canonicalName, [], true);
                canonicalName = recursed_1.canonicalName;
                // Add brackets if this is a tuple array and also add a comma
                canonicalName += "".concat(input.type.slice(5), ",");
                flatParam.push(recursed_1.def);
            }
            else {
                canonicalName += input.type;
                canonicalName += ',';
            }
            def.push(flatParam);
        });
        // Take off the last comma. Note that we do not want to slice if the last param was a tuple,
        // since we want to keep that `)`
        if (canonicalName[canonicalName.length - 1] === ',') {
            canonicalName = canonicalName.slice(0, canonicalName.length - 1);
        }
        // Add the closing parens
        canonicalName += ')';
    }
    return { def: def, canonicalName: canonicalName };
}
/**
 * Convert a set of EVMParamInfo objects into an array that can be serialized into decoder info that
 * can be passed with the signing request. NOTE: We do not know parameter names, so we just number
 * them
 * @internal
 */
function parseParamDef(def, prefix) {
    if (prefix === void 0) { prefix = ''; }
    var parsedDef = [];
    var numTuples = 0;
    def.forEach(function (param, i) {
        if (Array.isArray(param)) {
            // Arrays indicate nested params inside a tuple and always come after the initial tuple type
            // info. Recurse to parse nested tuple params and append them to the most recent.
            parsedDef[parsedDef.length - 1].push(parseParamDef(param, "".concat(i, "-")));
        }
        else {
            // If this is not tuple info, add the flat param info to the def
            parsedDef.push([
                "#".concat(prefix).concat(i + 1 - numTuples),
                param.typeIdx,
                param.szBytes,
                param.arraySzs,
            ]);
        }
        // Tuple
        if (param.typeIdx === EVM_TYPES.indexOf('tuple')) {
            numTuples += 1;
        }
    });
    return parsedDef;
}
/**
 * Convert a param into an EVMParamInfo object before flattening its data into an array.
 * @internal
 */
function getFlatParam(input) {
    if (!input.type) {
        throw new Error('No type in input');
    }
    var param = [input.name];
    var _a = getParamTypeInfo(input.type), typeIdx = _a.typeIdx, szBytes = _a.szBytes, arraySzs = _a.arraySzs;
    param.push(typeIdx);
    param.push(szBytes);
    param.push(arraySzs);
    return param;
}
/**
 * Convert a param type string into an EVMParamInfo object with attributes:
 * 1. paramName -     name of the parameter. This piece of data is unverified, so it will display
 *                    differently if the user has the function saved in secure storage.
 * 2. paramType -     basic type of param. Firmware has an enum with 7 values.
 * 3. paramSzBytes -  number of bytes representing this param. Only certain types can have nonzero
 *                    value for this. For example, a `uint` with a 4 in this slot would be uint32
 *                    (8*4 = 32). Maximum number of bytes is always 32 because these types can only
 *                    be used in single 32 byte words.
 * @internal
 */
function getParamTypeInfo(type) {
    var param = {
        szBytes: 0,
        typeIdx: 0,
        arraySzs: [],
    };
    var baseType;
    EVM_TYPES.forEach(function (t, i) {
        if (type.indexOf(t) > -1 && !baseType) {
            baseType = t;
            param.typeIdx = i;
        }
    });
    // Get the array size, if any
    param.arraySzs = getArraySzs(type);
    // Determine where to search for expanded size
    var szIdx = param.arraySzs.length > 0 ? type.indexOf('[') : type.length;
    if (['uint', 'int', 'bytes'].indexOf(baseType) > -1) {
        // If this can have a fixed size, capture that
        var szBits = parseInt(type.slice(baseType.length, szIdx)) || 0;
        if (szBits > 256) {
            throw new Error('Invalid param size');
        }
        param.szBytes = szBits / 8;
    }
    else {
        // No fixed size in the type
        param.szBytes = 0;
    }
    return param;
}
/**
 * Determine the dimensions of an array type. These dimensions can be either fixed or variable size.
 * Returns an array of sizes. Ex: uint256[][] -> [0, 0], uint256[1][3] -> [1, 3], uint256 -> []
 * @internal
 */
function getArraySzs(type) {
    if (typeof type !== 'string') {
        throw new Error('Invalid type');
    }
    var szs = [];
    var t1 = type;
    while (t1.length > 0) {
        var openIdx = t1.indexOf('[');
        if (openIdx < 0) {
            return szs;
        }
        var t2 = t1.slice(openIdx);
        var closeIdx = t2.indexOf(']');
        if (closeIdx < 0) {
            throw new Error('Bad param type');
        }
        var t3 = t2.slice(1, closeIdx);
        if (t3.length === 0) {
            // Variable size
            szs.push(0);
        }
        else {
            // Fixed size
            szs.push(parseInt(t3));
        }
        t1 = t2.slice(closeIdx + 1);
    }
    return szs;
}
/** @internal */
function getTupleName(name, withArr) {
    if (withArr === void 0) { withArr = true; }
    var brackets = 0, addedFirstBracket = false;
    for (var i = 0; i < name.length; i++) {
        if (name[i] === '(') {
            brackets += 1;
            addedFirstBracket = true;
        }
        else if (name[i] === ')') {
            brackets -= 1;
        }
        var canBreak = name[i + 1] === ',' || name[i + 1] === ')' || i === name.length - 1;
        if (!withArr && name[i + 1] === '[') {
            canBreak = true;
        }
        if (!brackets && addedFirstBracket && canBreak) {
            return name.slice(0, i + 1);
        }
    }
    throw new Error(BAD_CANONICAL_ERR);
}
/** @internal */
function isTuple(type) {
    return type[0] === '(';
}
var BAD_CANONICAL_ERR = 'Could not parse canonical function name.';
var EVM_TYPES = [
    null,
    'address',
    'bool',
    'uint',
    'int',
    'bytes',
    'string',
    'tuple',
];
